import EventBusyIcon from '@mui/icons-material/EventBusy'
import HistoryIcon from '@mui/icons-material/History'
import PeopleIcon from '@mui/icons-material/People'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import {
  Alert,
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { AppBarR, Wrapper } from '../components/common'
import db, { getOfficesManagedByUser } from '../db/db'
import PeopleService from '../services/PeopleService'
import { sessionState } from '../state'
import Database from '../types/Database'

import routes from './routes'

/**
 * People - People management page component
 *
 * Provides office managers with a view of office users,
 * manager assignment capability, and absence reporting for direct reports.
 */
const People: React.FC = () => {
  const session = useRecoilValue(sessionState)
  const { user, isAuthenticating } = session

  // Tab state
  const [activeTab, setActiveTab] = useState<'office-users' | 'my-reports'>(
    'office-users'
  )

  // Office selection state
  const [managedOffices, setManagedOffices] = useState<Database.Office[]>([])
  const [selectedOfficeId, setSelectedOfficeId] = useState<string>('')
  const [isLoadingOffices, setIsLoadingOffices] = useState(true)

  // Office users state
  const [officeUsers, setOfficeUsers] = useState<Database.User[]>([])
  const [managerAssignments, setManagerAssignments] = useState<
    Database.OfficeUserManager[]
  >([])
  const [isLoadingUsers, setIsLoadingUsers] = useState(false)

  // My Reports state
  const [myReports, setMyReports] = useState<Database.OfficeUserManager[]>([])
  const [reportUserDetails, setReportUserDetails] = useState<
    Map<string, Database.User>
  >(new Map())
  const [isLoadingReports, setIsLoadingReports] = useState(false)

  // Absence history state
  const [absenceReports, setAbsenceReports] = useState<
    Database.AbsenceReport[]
  >([])
  const [isLoadingAbsences, setIsLoadingAbsences] = useState(false)

  // Report Absent dialog state
  const [absenceDialogOpen, setAbsenceDialogOpen] = useState(false)
  const [absenceTarget, setAbsenceTarget] = useState<{
    userId: string
    name: string
  } | null>(null)
  const [absenceDate, setAbsenceDate] = useState(
    new Date().toISOString().split('T')[0]
  )
  const [absenceType, setAbsenceType] = useState<'full_day' | 'partial_day'>(
    'full_day'
  )
  const [absenceHours, setAbsenceHours] = useState<number>(1)
  const [absenceReason, setAbsenceReason] = useState<
    'arrived_late' | 'called_in_sick' | 'didnt_show_up'
  >('called_in_sick')
  const [absenceNotes, setAbsenceNotes] = useState('')
  const [isSubmittingAbsence, setIsSubmittingAbsence] = useState(false)

  // Feedback messages
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  // Load managed offices on mount
  useEffect(() => {
    const fetchManagedOffices = async () => {
      if (!user?.uid) return

      try {
        setIsLoadingOffices(true)
        const offices = await getOfficesManagedByUser(user.uid)
        setManagedOffices(offices)

        if (offices.length > 0 && offices[0].oid) {
          setSelectedOfficeId(offices[0].oid)
        }
      } catch (error) {
        console.error('Error loading managed offices:', error)
        setErrorMessage('Failed to load managed offices.')
      } finally {
        setIsLoadingOffices(false)
      }
    }

    fetchManagedOffices()
  }, [user?.uid])

  // Load office users and manager assignments when office is selected
  useEffect(() => {
    const fetchOfficeData = async () => {
      if (!selectedOfficeId) return

      try {
        setIsLoadingUsers(true)
        const results = await Promise.allSettled([
          PeopleService.getOfficeUsers(selectedOfficeId),
          PeopleService.getManagerAssignments(selectedOfficeId)
        ])

        if (results[0].status === 'fulfilled') {
          setOfficeUsers(results[0].value)
        } else {
          console.error('Error loading office users:', results[0].reason)
          setErrorMessage('Failed to load office users.')
        }

        if (results[1].status === 'fulfilled') {
          setManagerAssignments(results[1].value)
        } else {
          console.warn(
            'Could not load manager assignments (permissions may not be deployed yet):',
            results[1].reason
          )
          setManagerAssignments([])
        }
      } catch (error) {
        console.error('Error loading office data:', error)
        setErrorMessage('Failed to load office data.')
      } finally {
        setIsLoadingUsers(false)
      }
    }

    fetchOfficeData()
  }, [selectedOfficeId])

  // Load my reports when office is selected and tab is "my-reports"
  useEffect(() => {
    const fetchMyReports = async () => {
      if (!selectedOfficeId || !user?.uid || activeTab !== 'my-reports') return

      try {
        setIsLoadingReports(true)
        const reports = await PeopleService.getMyReports(
          selectedOfficeId,
          user.uid
        )
        setMyReports(reports)

        // Fetch user details directly from Firestore to avoid race condition
        // with the officeUsers state which may not be populated yet
        const detailsMap = new Map<string, Database.User>()
        const uniqueUserIds = [
          ...new Set(reports.map(report => report.userId))
        ]
        for (const userId of uniqueUserIds) {
          const userDoc = await db.getById('users', userId)
          if (userDoc) {
            detailsMap.set(userId, userDoc)
          }
        }
        setReportUserDetails(detailsMap)

        // Load absence reports
        setIsLoadingAbsences(true)
        const absences = await PeopleService.getAbsenceReports(selectedOfficeId)
        setAbsenceReports(absences)
        setIsLoadingAbsences(false)
      } catch (error) {
        console.error('Error loading my reports:', error)
        setErrorMessage('Failed to load reports.')
      } finally {
        setIsLoadingReports(false)
      }
    }

    fetchMyReports()
  }, [selectedOfficeId, user?.uid, activeTab])

  // Handle tab change
  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: 'office-users' | 'my-reports'
  ) => {
    setActiveTab(newValue)
    setSuccessMessage('')
    setErrorMessage('')
  }

  // Handle office selection change
  const handleOfficeChange = (officeId: string) => {
    setSelectedOfficeId(officeId)
    setSuccessMessage('')
    setErrorMessage('')
  }

  // Handle manager assignment
  const handleAssignManager = async (userId: string, managerId: string) => {
    if (!selectedOfficeId || !user?.uid) return

    try {
      setErrorMessage('')
      await PeopleService.assignManager(
        selectedOfficeId,
        userId,
        managerId,
        user.uid
      )
      setSuccessMessage('Manager assigned successfully.')

      // Reload assignments
      const assignments = await PeopleService.getManagerAssignments(
        selectedOfficeId
      )
      setManagerAssignments(assignments)
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to assign manager'
      setErrorMessage(errorMsg)
    }
  }

  // Handle remove manager assignment
  const handleRemoveManager = async (assignmentId: string) => {
    try {
      setErrorMessage('')
      await PeopleService.removeManagerAssignment(assignmentId)
      setSuccessMessage('Manager assignment removed.')

      // Reload assignments
      const assignments = await PeopleService.getManagerAssignments(
        selectedOfficeId
      )
      setManagerAssignments(assignments)
    } catch (error) {
      const errorMsg =
        error instanceof Error
          ? error.message
          : 'Failed to remove manager assignment'
      setErrorMessage(errorMsg)
    }
  }

  // Open absence dialog
  const handleOpenAbsenceDialog = (userId: string, name: string) => {
    setAbsenceTarget({ userId, name })
    setAbsenceDate(new Date().toISOString().split('T')[0])
    setAbsenceType('full_day')
    setAbsenceHours(1)
    setAbsenceReason('called_in_sick')
    setAbsenceNotes('')
    setAbsenceDialogOpen(true)
  }

  // Close absence dialog
  const handleCloseAbsenceDialog = () => {
    setAbsenceDialogOpen(false)
    setAbsenceTarget(null)
  }

  // Submit absence report
  const handleSubmitAbsence = async () => {
    if (!absenceTarget || !selectedOfficeId || !user?.uid) return

    try {
      setIsSubmittingAbsence(true)
      setErrorMessage('')

      await PeopleService.reportAbsence({
        officeId: selectedOfficeId,
        userId: absenceTarget.userId,
        reportedBy: user.uid,
        date: absenceDate,
        type: absenceType,
        hours: absenceType === 'partial_day' ? absenceHours : null,
        reason: absenceReason,
        createdAt: new Date(),
        notes: absenceNotes || undefined
      })

      setSuccessMessage(
        `Absence reported for ${absenceTarget.name} successfully.`
      )
      handleCloseAbsenceDialog()

      // Reload absence reports
      const absences = await PeopleService.getAbsenceReports(selectedOfficeId)
      setAbsenceReports(absences)
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : 'Failed to report absence'
      setErrorMessage(errorMsg)
    } finally {
      setIsSubmittingAbsence(false)
    }
  }

  // Helper to get user display name
  const getUserDisplayName = (userObj: Database.User | undefined): string => {
    if (!userObj) return 'Unknown User'
    const name =
      `${userObj.firstname || ''} ${userObj.lastname || ''}`.trim()
    return name || userObj.email || 'Unknown User'
  }

  // Helper to get manager name for a user
  const getManagerForUser = (
    userId: string
  ): { assignment: Database.OfficeUserManager | undefined; name: string } => {
    const assignment = managerAssignments.find(a => a.userId === userId)
    if (!assignment) return { assignment: undefined, name: '' }

    const manager = officeUsers.find(u => u.uid === assignment.managerId)
    return {
      assignment,
      name: getUserDisplayName(manager)
    }
  }

  // Format reason for display
  const formatReason = (
    reason: 'arrived_late' | 'called_in_sick' | 'didnt_show_up'
  ): string => {
    switch (reason) {
      case 'arrived_late':
        return 'Arrived Late'
      case 'called_in_sick':
        return 'Called in Sick'
      case 'didnt_show_up':
        return "Didn't Show Up"
      default:
        return reason
    }
  }

  // Redirect unauthenticated users to sign-in page
  if (!isAuthenticating && !user) {
    return <Navigate to={routes.signin} replace />
  }

  // Show loading state during authentication
  if (isAuthenticating) {
    return (
      <Backdrop
        sx={{
          color: theme => (theme.palette.mode === 'dark' ? '#fff' : '#000'),
          zIndex: 9999,
          position: 'fixed',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? 'rgba(0, 0, 0, 0.95)'
              : 'rgba(255, 255, 255, 0.95)'
        }}
        open={true}
      >
        <CircularProgress color='inherit' size={60} />
      </Backdrop>
    )
  }

  return (
    <>
      <AppBarR />
      <Wrapper>
        <Box sx={{ p: 2 }}>
          {/* Page Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <PeopleIcon fontSize='small' />
            </Avatar>
            <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
              People
            </Typography>
          </Box>

          <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
            Manage office users, assign managers, and report absences
          </Typography>

          {/* Loading offices */}
          {isLoadingOffices && (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              minHeight='200px'
            >
              <CircularProgress />
              <Typography variant='body1' sx={{ ml: 2 }}>
                Loading offices...
              </Typography>
            </Box>
          )}

          {/* No managed offices */}
          {!isLoadingOffices && managedOffices.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <PeopleIcon
                sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
              />
              <Typography variant='body1' color='text.secondary'>
                You are not a manager or owner of any office.
              </Typography>
            </Paper>
          )}

          {/* Main content when offices are available */}
          {!isLoadingOffices && managedOffices.length > 0 && (
            <>
              {/* Office selector */}
              {managedOffices.length > 1 && (
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel id='office-select-label'>
                    Select Office
                  </InputLabel>
                  <Select
                    labelId='office-select-label'
                    value={selectedOfficeId}
                    label='Select Office'
                    onChange={e => handleOfficeChange(e.target.value)}
                  >
                    {managedOffices.map(office => (
                      <MenuItem key={office.oid} value={office.oid || ''}>
                        {office.name || 'Unnamed Office'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Success/Error messages */}
              {successMessage && (
                <Alert
                  severity='success'
                  sx={{ mb: 2 }}
                  onClose={() => setSuccessMessage('')}
                >
                  {successMessage}
                </Alert>
              )}
              {errorMessage && (
                <Alert
                  severity='error'
                  sx={{ mb: 2 }}
                  onClose={() => setErrorMessage('')}
                >
                  {errorMessage}
                </Alert>
              )}

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab label='Office Users' value='office-users' />
                <Tab label='My Reports' value='my-reports' />
              </Tabs>

              {/* Office Users Tab */}
              {activeTab === 'office-users' && (
                <Paper sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2
                    }}
                  >
                    <PersonAddIcon color='primary' />
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                      Office Users
                    </Typography>
                  </Box>

                  {isLoadingUsers ? (
                    <Box
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      minHeight='100px'
                    >
                      <CircularProgress size={24} />
                      <Typography variant='body2' sx={{ ml: 2 }}>
                        Loading users...
                      </Typography>
                    </Box>
                  ) : officeUsers.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <PeopleIcon
                        sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                      />
                      <Typography variant='body2' color='text.secondary'>
                        No users found in this office.
                      </Typography>
                    </Box>
                  ) : (
                    <List>
                      {officeUsers.map(officeUser => {
                        const displayName = getUserDisplayName(officeUser)
                        const { assignment, name: managerName } =
                          getManagerForUser(officeUser.uid || '')

                        return (
                          <ListItem
                            key={officeUser.uid || officeUser.id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              mb: 1,
                              flexDirection: 'column',
                              alignItems: 'stretch'
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%',
                                mb: 1
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {displayName.charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={displayName}
                                secondary={
                                  <>
                                    {officeUser.email}
                                    {managerName && (
                                      <>
                                        {' | Manager: '}
                                        {managerName}
                                      </>
                                    )}
                                  </>
                                }
                              />
                            </Box>

                            {/* Manager Assignment */}
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                pl: 7
                              }}
                            >
                              <FormControl size='small' sx={{ minWidth: 200 }}>
                                <InputLabel id={`manager-select-${officeUser.uid}`}>
                                  Assign Manager
                                </InputLabel>
                                <Select
                                  labelId={`manager-select-${officeUser.uid}`}
                                  value={assignment?.managerId || ''}
                                  label='Assign Manager'
                                  onChange={e => {
                                    if (officeUser.uid) {
                                      handleAssignManager(
                                        officeUser.uid,
                                        e.target.value
                                      )
                                    }
                                  }}
                                >
                                  {officeUsers
                                    .filter(u => u.uid !== officeUser.uid)
                                    .map(potentialManager => (
                                      <MenuItem
                                        key={potentialManager.uid}
                                        value={potentialManager.uid || ''}
                                      >
                                        {getUserDisplayName(potentialManager)}
                                      </MenuItem>
                                    ))}
                                </Select>
                              </FormControl>
                              {assignment && (
                                <Button
                                  size='small'
                                  color='error'
                                  variant='outlined'
                                  onClick={() =>
                                    handleRemoveManager(assignment.id)
                                  }
                                >
                                  Remove
                                </Button>
                              )}
                            </Box>
                          </ListItem>
                        )
                      })}
                    </List>
                  )}
                </Paper>
              )}

              {/* My Reports Tab */}
              {activeTab === 'my-reports' && (
                <Box>
                  <Paper sx={{ p: 3, mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}
                    >
                      <PeopleIcon color='primary' />
                      <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        My Reports
                      </Typography>
                    </Box>

                    {isLoadingReports ? (
                      <Box
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                        minHeight='100px'
                      >
                        <CircularProgress size={24} />
                        <Typography variant='body2' sx={{ ml: 2 }}>
                          Loading reports...
                        </Typography>
                      </Box>
                    ) : myReports.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <PeopleIcon
                          sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          No one is assigned to you as their manager in this
                          office.
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {myReports.map(report => {
                          const userDetail = reportUserDetails.get(
                            report.userId
                          )
                          const displayName = getUserDisplayName(userDetail)

                          return (
                            <ListItem
                              key={report.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1
                              }}
                              secondaryAction={
                                <Button
                                  variant='contained'
                                  size='small'
                                  color='warning'
                                  startIcon={<EventBusyIcon />}
                                  onClick={() =>
                                    handleOpenAbsenceDialog(
                                      report.userId,
                                      displayName
                                    )
                                  }
                                >
                                  Report Absent
                                </Button>
                              }
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                  {displayName.charAt(0).toUpperCase()}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={displayName}
                                secondary={userDetail?.email || ''}
                              />
                            </ListItem>
                          )
                        })}
                      </List>
                    )}
                  </Paper>

                  {/* Absence History Section */}
                  <Paper sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 2
                      }}
                    >
                      <HistoryIcon color='primary' />
                      <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        Absence History
                      </Typography>
                    </Box>

                    {isLoadingAbsences ? (
                      <Box
                        display='flex'
                        justifyContent='center'
                        alignItems='center'
                        minHeight='100px'
                      >
                        <CircularProgress size={24} />
                        <Typography variant='body2' sx={{ ml: 2 }}>
                          Loading absence history...
                        </Typography>
                      </Box>
                    ) : absenceReports.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <HistoryIcon
                          sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                        />
                        <Typography variant='body2' color='text.secondary'>
                          No absence reports recorded yet.
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {absenceReports.map(absence => {
                          const absentUser = officeUsers.find(
                            u => u.uid === absence.userId
                          )
                          const absentUserName =
                            getUserDisplayName(absentUser)

                          return (
                            <ListItem
                              key={absence.id}
                              sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                mb: 1
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'warning.main' }}>
                                  <EventBusyIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={`${absentUserName} - ${formatReason(absence.reason)}`}
                                secondary={`${absence.date} | ${
                                  absence.type === 'full_day'
                                    ? 'Full Day'
                                    : `${absence.hours} hours`
                                }${absence.notes ? ` | ${absence.notes}` : ''}`}
                              />
                            </ListItem>
                          )
                        })}
                      </List>
                    )}
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>

        {/* Report Absent Dialog */}
        <Dialog
          open={absenceDialogOpen}
          onClose={handleCloseAbsenceDialog}
          maxWidth='sm'
          fullWidth
        >
          <DialogTitle>
            Report Absence - {absenceTarget?.name || ''}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Date picker */}
              <TextField
                label='Absence Date'
                type='date'
                value={absenceDate}
                onChange={e => setAbsenceDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              {/* Full Day / Partial Day toggle */}
              <FormControl>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Absence Duration
                </Typography>
                <RadioGroup
                  value={absenceType}
                  onChange={e =>
                    setAbsenceType(
                      e.target.value as 'full_day' | 'partial_day'
                    )
                  }
                >
                  <FormControlLabel
                    value='full_day'
                    control={<Radio />}
                    label='Full Day'
                  />
                  <FormControlLabel
                    value='partial_day'
                    control={<Radio />}
                    label='Set Hours'
                  />
                </RadioGroup>
              </FormControl>

              {/* Hours input (only shown for partial day) */}
              {absenceType === 'partial_day' && (
                <TextField
                  label='Hours'
                  type='number'
                  value={absenceHours}
                  onChange={e =>
                    setAbsenceHours(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  inputProps={{ min: 1, max: 23 }}
                  fullWidth
                />
              )}

              {/* Reason dropdown */}
              <FormControl fullWidth>
                <InputLabel id='absence-reason-label'>Reason</InputLabel>
                <Select
                  labelId='absence-reason-label'
                  value={absenceReason}
                  label='Reason'
                  onChange={e =>
                    setAbsenceReason(
                      e.target.value as
                        | 'arrived_late'
                        | 'called_in_sick'
                        | 'didnt_show_up'
                    )
                  }
                >
                  <MenuItem value='arrived_late'>Arrived Late</MenuItem>
                  <MenuItem value='called_in_sick'>Called in Sick</MenuItem>
                  <MenuItem value='didnt_show_up'>
                    Didn&apos;t Show Up
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Notes field */}
              <TextField
                label='Notes (optional)'
                value={absenceNotes}
                onChange={e => setAbsenceNotes(e.target.value)}
                multiline
                rows={3}
                fullWidth
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAbsenceDialog}
              disabled={isSubmittingAbsence}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAbsence}
              variant='contained'
              disabled={isSubmittingAbsence || !absenceDate}
            >
              {isSubmittingAbsence ? (
                <CircularProgress size={24} />
              ) : (
                'Submit'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Wrapper>
    </>
  )
}

export default People
